import { useMemo, useState } from 'react';
import { AssignmentDrawer, type DrawerTarget } from '../components/AssignmentDrawer';
import { StackedColumns, type StackedSeries } from '../components/chart/StackedColumns';
import { SERIES_SLOTS, colorFor } from '../components/chart/palette';
import { WORK_MODE_LABELS, formatMonthKey } from '../lib/format';
import type { AggregateBundle, AreaId, Filters, Meta } from '../types';

interface TrendViewProps {
  stats: AggregateBundle;
  filters: Filters;
  meta: Meta | undefined;
  onFilterChange: (patch: Partial<Filters>) => void;
  onShowAssignments: (patch: Partial<Filters>) => void;
}

const MAX_AREA_SERIES = SERIES_SLOTS;
const MOVERS_PER_DIRECTION = 8;
/** Under den här gränsen är procenttalen brus snarare än trend. */
const MIN_COUNT_FOR_MOVER = 3;

/**
 * Trender svarar på en annan fråga än Översikt: inte "hur ser marknaden ut"
 * utan "vad förändras". Varje panel är vald för att kunna påverka ett beslut –
 * vad ska vi kompetensutveckla, vem ska vi rekrytera, var flyttar efterfrågan.
 */
export function TrendView({ stats, filters, meta, onFilterChange, onShowAssignments }: TrendViewProps) {
  const [drawerTarget, setDrawerTarget] = useState<DrawerTarget | null>(null);

  const { movers, fallers, hasMeasurableTrend } = useMemo(() => {
    const measurable = stats.technologies.filter(
      (tech) => tech.trendPct !== null && tech.count >= MIN_COUNT_FOR_MOVER,
    );
    const sorted = [...measurable].sort((a, b) => (b.trendPct ?? 0) - (a.trendPct ?? 0));
    return {
      movers: sorted.filter((t) => (t.trendPct ?? 0) > 0).slice(0, MOVERS_PER_DIRECTION),
      fallers: sorted.filter((t) => (t.trendPct ?? 0) < 0).slice(-MOVERS_PER_DIRECTION).reverse(),
      hasMeasurableTrend: measurable.length > 0,
    };
  }, [stats.technologies]);

  // Utan mätbar trend visar vi i stället vad som faktiskt efterfrågats de
  // senaste 30 dagarna. Det är en sannare rubrik än en påhittad procentsats.
  const recent = useMemo(
    () => stats.technologies.filter((t) => t.currentCount > 0).sort((a, b) => b.currentCount - a.currentCount).slice(0, 12),
    [stats.technologies],
  );

  // Områden utan återkommande kombinationer hoppas över helt – en rubrik med
  // en tom lista under säger ingenting.
  const pairAreas = useMemo(
    () => stats.areas.filter((area) => (stats.techPairsByArea[area.id] ?? []).length > 0),
    [stats.areas, stats.techPairsByArea],
  );

  const areaShare = useMemo(() => buildShare(stats, MAX_AREA_SERIES), [stats]);
  const workModeShare = useMemo(() => buildWorkModeShare(stats), [stats]);

  return (
    <>
      <div className="panel-grid panel-grid--split">        <section className="panel">
          <div className="panel__header">
            <h2 className="panel__title">{hasMeasurableTrend ? 'Rör sig uppåt' : 'Mest efterfrågat senaste 30 dagarna'}</h2>
          </div>
          <p className="panel__subtitle">
            {hasMeasurableTrend
              ? 'Senaste 30 dagarna mot de 30 dagarna dessförinnan, räknat på verkliga publiceringsdatum.'
              : 'Jämförelseperioden har ännu för få daterade uppdrag för att räkna trend. Tills dess visas volymen i stället – trendpilarna tänds av sig själva när underlaget räcker.'}
          </p>
          <MoverList
            rows={hasMeasurableTrend ? movers : recent.map((t) => ({ ...t, trendPct: null }))}
            onSelect={(name) => setDrawerTarget({ tech: name })}
          />
        </section>        <section className="panel">
          <div className="panel__header">
            <h2 className="panel__title">Arbetssätt över tid</h2>
            <span className="panel__tools">Andel av de uppdrag som anger det</span>
          </div>
          <p className="panel__subtitle">
            Hur stor andel som är på plats, hybrid eller distans. Uppdrag utan uppgift räknas inte med.
          </p>
          {workModeShare.months.length === 0 ? (
            <p className="empty">Ingen källa i urvalet anger arbetssätt.</p>
          ) : (
            <>
              <StackedColumns
                categories={workModeShare.months}
                categoryLabels={workModeShare.months.map(formatMonthKey)}
                series={workModeShare.series}
                values={workModeShare.values}
                height={300}
                valueSuffix=" %"
              />
              <div className="legend">
                {workModeShare.series.map((s) => (
                  <span key={s.key} className="legend__item">
                    <span className="legend__swatch" style={{ background: s.color }} aria-hidden="true" />
                    {s.label}
                  </span>
                ))}
              </div>
            </>
          )}
        </section>
        <section className="panel" style={{ gridColumn: '1 / -1' }}>
          <div className="panel__header">
            <h2 className="panel__title">Kompetenspar som lönar sig</h2>
            <span className="panel__tools">Räknat inom varje område</span>
          </div>
          <p className="panel__subtitle">
            Tekniker som efterfrågas i samma uppdrag. En konsult som behärskar båda matchar fler uppdrag än två
            specialister var för sig. Paren räknas inom varje område för sig – ett par som är självklart i Utveckling
            kan vara nyheten i Test.
          </p>
          {pairAreas.length === 0 ? (
            <p className="empty">För få uppdrag i urvalet för att hitta återkommande kombinationer.</p>
          ) : (
            <div className="pair-areas">
              {pairAreas.map((area) => (
                <div key={area.id} className="pair-areas__area">
                  <button
                    type="button"
                    className="pair-areas__name"
                    onClick={() => onFilterChange({ area: filters.area === area.id ? undefined : area.id })}
                  >
                    {area.name}
                  </button>
                  <ol className="pair-list">
                    {(stats.techPairsByArea[area.id] ?? []).map((pair) => (
                      <li key={`${area.id}-${pair.a}-${pair.b}`} className="pair">
                        <button
                          type="button"
                          className="pair__names"
                          onClick={() => onShowAssignments({ tech: [pair.a, pair.b], area: area.id })}
                          title={`Visa de ${pair.count} uppdrag inom ${area.name} som kräver både ${pair.a} och ${pair.b}`}
                        >
                          <span className="tag tag--match">{pair.a}</span>
                          <span className="pair__plus" aria-hidden="true">
                            +
                          </span>
                          <span className="tag tag--match">{pair.b}</span>
                        </button>
                        <span className="pair__count">{pair.count}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          )}
        </section>
        <section className="panel" style={{ gridColumn: '1 / -1' }}>
          <div className="panel__header">
            <h2 className="panel__title">Områdenas andel över tid</h2>
            <span className="panel__tools">Andel av månadens uppdrag</span>
          </div>
          <p className="panel__subtitle">
            Normaliserat till 100 % per månad. Till skillnad från Översikt visar det här strukturella skiften även när
            den totala volymen svänger – en månad med få uppdrag väger lika mycket som en med många.
          </p>
          <StackedColumns
            categories={stats.months}
            categoryLabels={stats.months.map(formatMonthKey)}
            series={areaShare.series}
            values={areaShare.values}
            height={300}
            valueSuffix=" %"
          />
          <div className="legend">
            {areaShare.series.map((s) => (
              <button
                key={s.key}
                type="button"
                className="legend__item"
                onClick={() => s.key !== '__other' && onFilterChange({ area: s.key as AreaId })}
              >
                <span className="legend__swatch" style={{ background: s.color }} aria-hidden="true" />
                {s.label}
              </button>
            ))}
          </div>
        </section>

        {hasMeasurableTrend && fallers.length > 0 ? (
          <section className="panel">
            <div className="panel__header">
              <h2 className="panel__title">Rör sig nedåt</h2>
            </div>
            <p className="panel__subtitle">Tekniker som efterfrågas mindre än under föregående period.</p>
            <MoverList rows={fallers} onSelect={(name) => setDrawerTarget({ tech: name })} />
          </section>
        ) : null}
      </div>

      <AssignmentDrawer
        target={drawerTarget}
        filters={filters}
        meta={meta}
        onClose={() => setDrawerTarget(null)}
        onShowAll={onShowAssignments}
      />
    </>
  );
}

function MoverList({
  rows,
  onSelect,
}: {
  rows: { name: string; category: string; count: number; currentCount: number; previousCount: number; trendPct: number | null }[];
  onSelect: (name: string) => void;
}) {
  if (rows.length === 0) return <p className="empty">Inget att visa i urvalet.</p>;

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <tr>
            <th>Teknik</th>
            <th className="num">Nu</th>
            <th className="num">Innan</th>
            <th className="num">Förändring</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((tech) => (
            <tr key={tech.name}>
              <td>
                <button type="button" className="row-link" onClick={() => onSelect(tech.name)}>
                  {tech.name}
                </button>
                <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{tech.category}</div>
              </td>
              <td className="num">{tech.currentCount}</td>
              <td className="num" style={{ color: 'var(--text-secondary)' }}>
                {tech.trendPct === null ? '–' : tech.previousCount}
              </td>
              <td className="num">
                {tech.trendPct === null ? (
                  <span className="delta delta--flat">–</span>
                ) : (
                  <span className={`delta delta--${tech.trendPct > 0 ? 'up' : tech.trendPct < 0 ? 'down' : 'flat'}`}>
                    <span aria-hidden="true">{tech.trendPct > 0 ? '▲' : tech.trendPct < 0 ? '▼' : '■'}</span>
                    {Math.abs(tech.trendPct)} %
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Gör om månadens absoluta tal till procentandelar som summerar till 100. */
function toPercentages(counts: Record<string, number>, keys: string[]): Record<string, number> {
  const total = keys.reduce((sum, key) => sum + (counts[key] ?? 0), 0);
  if (total === 0) return {};

  const row: Record<string, number> = {};
  for (const key of keys) {
    const value = counts[key] ?? 0;
    if (value > 0) row[key] = Math.round((value / total) * 100);
  }
  return row;
}

function buildShare(stats: AggregateBundle, maxSeries: number) {
  const ranked = stats.areas.filter((area) => area.count > 0);
  const named = ranked.slice(0, maxSeries);
  const folded = ranked.slice(maxSeries);

  const series: StackedSeries[] = named.map((area, index) => ({
    key: area.id,
    label: area.name,
    color: colorFor(index),
  }));
  if (folded.length > 0) {
    series.push({ key: '__other', label: `Övriga (${folded.length})`, color: colorFor(maxSeries) });
  }

  const values = stats.months.map((monthKey) => {
    const byArea = stats.areaByMonth[monthKey] ?? {};
    const counts: Record<string, number> = {};
    for (const area of named) counts[area.id] = byArea[area.id] ?? 0;
    if (folded.length > 0) counts.__other = folded.reduce((sum, area) => sum + (byArea[area.id] ?? 0), 0);
    return toPercentages(counts, series.map((s) => s.key));
  });

  return { series, values };
}

const WORK_MODE_ORDER = ['onsite', 'hybrid', 'remote'];

function buildWorkModeShare(stats: AggregateBundle) {
  const series: StackedSeries[] = WORK_MODE_ORDER.map((mode, index) => ({
    key: mode,
    label: WORK_MODE_LABELS[mode] ?? mode,
    color: colorFor(index),
  }));

  // Bara månader där någon källa faktiskt angett arbetssätt tas med – annars
  // skulle tomma staplar se ut som att alla uppdrag plötsligt blev okända.
  const months = stats.months.filter((monthKey) => {
    const counts = stats.workModeByMonth[monthKey] ?? {};
    return WORK_MODE_ORDER.some((mode) => (counts[mode] ?? 0) > 0);
  });

  const values = months.map((monthKey) => {
    const counts = stats.workModeByMonth[monthKey] ?? {};
    return toPercentages(counts as Record<string, number>, WORK_MODE_ORDER);
  });

  return { series, values, months };
}
